const inviteKeys = new Set(["rivalInvite", "guestClaim"]);
const shortClaimPath = /^\/s\/([A-Za-z0-9_-]{32,200})$/;

/**
 * The result SMS carries the short form; the Rivalry hub stays the only reader
 * of a claim, so the entry point rewrites the path before the first render.
 */
export function claimLinkTarget(pathname: string): string | null {
  const match = shortClaimPath.exec(pathname);
  return match ? `/rivals?guestClaim=${encodeURIComponent(match[1]!)}` : null;
}

/** True when the URL carries a rival invite or a guest claim to accept. */
export function hasInviteLink(search: string): boolean {
  const query = new URLSearchParams(search);
  return [...query.keys()].some((key) => inviteKeys.has(key));
}

/** Preserve the intended destination through account recovery and email verification. */
export function hasAuthenticationLink(search: string): boolean {
  const result = new URLSearchParams(search).get("result");
  return (
    hasInviteLink(search) ||
    Boolean(
      result &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
        result,
      ),
    )
  );
}

/** Only known same-origin destinations survive recovery; never accept a redirect URL. */
export function passwordResetDestination(search: string): string {
  return preserveAuthenticationLink("/", search);
}

/** Carry only pending account destinations while navigating the saved local round. */
export function preserveAuthenticationLink(
  path: string,
  search: string,
): string {
  const [pathname, existing] = path.split("?");
  const query = new URLSearchParams(search);
  const destination = new URLSearchParams(existing);
  for (const key of ["guestClaim", "rivalInvite", "result"]) {
    const value = query.get(key);
    if (value) destination.set(key, value);
  }
  return destination.size ? `${pathname}?${destination}` : pathname!;
}

export function authenticationLinkContext(
  search: string,
): { title: string; intro: string } | null {
  const query = new URLSearchParams(search);
  if (query.has("guestClaim"))
    return {
      title: "Your round is already here.",
      intro:
        "Sign in to link your saved rounds. Already have an account? Use it to keep your history together. New here? Create an account below.",
    };
  if (query.has("rivalInvite"))
    return {
      title: "A rival invited you.",
      intro:
        "Sign in or create an account to see who invited you. You can review the invitation before accepting and opening your shared record.",
    };
  if (hasAuthenticationLink(search))
    return {
      title: "Continue to your scorecard.",
      intro:
        "Sign in to open the selected result with your account’s existing access. Your scorecard link will stay here while you sign in.",
    };
  return null;
}
